import _slicedToArray from "/runner/_work/developers/developers/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { Palette } from "../../constants/palettes";
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCookieBannerHeight } from "../cookie-banner";
import { useIsMenuScrolled } from "./helpers/use-is-menu-scrolled";
import { useIsMobileMenu } from "./helpers/use-is-mobile-menu";
import { DEBOUNCE_THRESHOLD, TRACKING_THROTTLE_INTERVAL } from "./constants";
import { jsx as _jsx } from "react/jsx-runtime";
var MenuContext = /*#__PURE__*/createContext({
  palette: Palette.Positive,
  transparentBackground: true,
  isMobileMenu: false,
  isMobileMenuOpen: false,
  isMenuScrolled: false,
  isUserDropdownVisible: false,
  activeSubMenuItem: null,
  cookieBannerHeight: 0,
  setActiveSubMenuItem: function setActiveSubMenuItem() {},
  setIsMobileMenuOpen: function setIsMobileMenuOpen() {}
});
export var useMenuContext = function useMenuContext() {
  return useContext(MenuContext);
};
var MegaMenuProvider = function MegaMenuProvider(_ref) {
  var children = _ref.children,
    palette = _ref.palette,
    transparentBackground = _ref.transparentBackground,
    hasUser = _ref.hasUser,
    hasUserMenuItems = _ref.hasUserMenuItems,
    isFetchingUser = _ref.isFetchingUser,
    onUserMenuClick = _ref.onUserMenuClick,
    onUserMenuItemClick = _ref.onUserMenuItemClick,
    onMenuItemClick = _ref.onMenuItemClick,
    onSubMenuItemClick = _ref.onSubMenuItemClick;
  var isMenuScrolled = useIsMenuScrolled();
  var isMobileMenu = useIsMobileMenu();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMobileMenuOpen = _useState2[0],
    setIsMobileMenuOpen = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    activeSubMenuItem = _useState4[0],
    setActiveSubMenuItem = _useState4[1];
  var subMenuElementsRef = useRef({});
  var cookieBannerHeight = useCookieBannerHeight() || 0;
  var throttledOnMenuItemClick = useMemo(function () {
    return onMenuItemClick ? throttle(onMenuItemClick, TRACKING_THROTTLE_INTERVAL, {
      leading: false
    }) : null;
  }, [onMenuItemClick]);
  var debouncedSetActiveSubMenuItem = useMemo(function () {
    return debounce(setActiveSubMenuItem, DEBOUNCE_THRESHOLD);
  }, []);
  var isUserDropdownVisible = useMemo(function () {
    if (!hasUserMenuItems) {
      return false;
    }
    return hasUser || isFetchingUser;
  }, [hasUserMenuItems, hasUser, isFetchingUser]);
  useEffect(function () {
    if (!isMobileMenu) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobileMenu]);
  useEffect(function () {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileMenu, isMobileMenuOpen]);
  useEffect(function () {
    var _throttledOnMenuItemC, _debouncedSetActiveSu;
    throttledOnMenuItemClick === null || throttledOnMenuItemClick === void 0 ? void 0 : (_throttledOnMenuItemC = throttledOnMenuItemClick.cancel) === null || _throttledOnMenuItemC === void 0 ? void 0 : _throttledOnMenuItemC.call(throttledOnMenuItemClick);
    debouncedSetActiveSubMenuItem === null || debouncedSetActiveSubMenuItem === void 0 ? void 0 : (_debouncedSetActiveSu = debouncedSetActiveSubMenuItem.cancel) === null || _debouncedSetActiveSu === void 0 ? void 0 : _debouncedSetActiveSu.call(debouncedSetActiveSubMenuItem);
    if (!!activeSubMenuItem && throttledOnMenuItemClick) {
      throttledOnMenuItemClick(activeSubMenuItem);
    }
  }, [activeSubMenuItem, debouncedSetActiveSubMenuItem, throttledOnMenuItemClick]);
  var contextValue = useMemo(function () {
    return {
      palette: palette,
      transparentBackground: transparentBackground,
      isMobileMenu: isMobileMenu,
      isMobileMenuOpen: isMobileMenuOpen,
      isMenuScrolled: isMenuScrolled,
      isUserDropdownVisible: isUserDropdownVisible,
      activeSubMenuItem: activeSubMenuItem,
      cookieBannerHeight: cookieBannerHeight,
      subMenuElementsRef: subMenuElementsRef,
      setIsMobileMenuOpen: setIsMobileMenuOpen,
      setActiveSubMenuItem: isMobileMenu ? setActiveSubMenuItem : debouncedSetActiveSubMenuItem,
      onUserMenuClick: onUserMenuClick,
      onUserMenuItemClick: onUserMenuItemClick,
      onMenuItemClick: onMenuItemClick,
      onSubMenuItemClick: onSubMenuItemClick
    };
  }, [activeSubMenuItem, cookieBannerHeight, debouncedSetActiveSubMenuItem, isMenuScrolled, isMobileMenu, isMobileMenuOpen, isUserDropdownVisible, onMenuItemClick, onSubMenuItemClick, onUserMenuClick, onUserMenuItemClick, palette, transparentBackground]);
  return /*#__PURE__*/_jsx(MenuContext.Provider, {
    value: contextValue,
    children: children
  });
};
MegaMenuProvider.displayName = 'MegaMenuProvider';
export default /*#__PURE__*/memo(MegaMenuProvider);